/* eslint-disable @next/next/no-img-element */
"use client";

import { useEffect, useMemo, useState } from "react";

type Review = {
  reviewId: string;
  authorName: string;
  rating: number;
  text: string;
  relativeTime: string;
};

type PlaceDoc = {
  placeId: string;
  reviews: Review[];
};

export default function GoogleReview() {
  const [places, setPlaces] = useState<PlaceDoc[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  /* ================= FETCH (SAFE) ================= */
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const base = process.env.NEXT_PUBLIC_API_URL;

        if (!base) {
          console.error("NEXT_PUBLIC_API_URL is missing");
          return;
        }

        const res = await fetch(
          `${base}/api/v1/review/google-reviews`,
          { cache: "no-store" }
        );

        if (!res.ok) {
          throw new Error(`API failed with status ${res.status}`);
        }

        const json = await res.json();

        if (mounted && Array.isArray(json?.data)) {
          setPlaces(json.data);
        }
      } catch (err) {
        console.error("Google reviews fetch failed", err);
        // Optional: setPlaces([]);  // keep UI stable
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  /* ================= FLATTEN ================= */
  const allReviews = useMemo<Review[]>(() => {
    return places.flatMap((p) =>
      Array.isArray(p.reviews) ? p.reviews : []
    );
  }, [places]);

  if (!allReviews.length) return null;

  const looped = [...allReviews, ...allReviews];

  const Stars = ({ rating }: { rating: number }) => {
    const r = Math.max(0, Math.min(5, Math.round(rating)));
    return (
      <div className="text-yellow-500 text-sm">
        {"★".repeat(r)}
        {"☆".repeat(5 - r)}
      </div>
    );
  };

  return (
    <section className="w-full overflow-hidden">
      <div className="mx-auto container px-4">
        <h2 className="mb-6 text-lg md:text-xl font-medium judson-regular text-gray-600 tracking-wide">
          Trusted by Customers on Google
        </h2>

        <div className="relative overflow-hidden">
          <div className="flex gap-4 animate-marquee hover:paused">
            {looped.map((r, i) => {
              const active = activeId === r.reviewId;

              return (
                <div
                  key={`${r.reviewId}-${i}`}
                  onMouseEnter={() => setActiveId(r.reviewId)}
                  onMouseLeave={() => setActiveId(null)}
                  className={`
                    shrink-0 rounded-xl border bg-white p-4 shadow-sm cursor-pointer
                    transition-all duration-300 ease-out
                    ${active ? "w-[300px] scale-[1.04]" : "w-[260px]"}
                  `}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{r.authorName}</p>
                      <p className="text-xs text-gray-500">{r.relativeTime}</p>
                    </div>

                    <img
                      src="https://www.gstatic.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
                      alt="Google"
                      className="h-5"
                    />
                  </div>

                  <Stars rating={r.rating} />

                  <p
                    className={`mt-2 text-sm text-gray-700 ${
                      active ? "line-clamp-none" : "line-clamp-3"
                    }`}
                  >
                    {r.text || "No comment"}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes marquee {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }

        .animate-marquee {
          width: max-content;
          animation: marquee 45s linear infinite;
        }

        @media (max-width: 768px) {
          .animate-marquee {
            animation-duration: 65s;
          }
        }
      `}</style>
    </section>
  );
}
